import Position from "./Position.js";
import CollisionMark from "./CollisionMark.js";

const POINT_SIZE = 10;
const MAX_TRAIL_LENGTH = 15;

export default
    class LivingEntity {
    constructor(x, y, id, world) {
        this.x = x;
        this.y = y;
        this.id = id;
        this.trail = [];
        this.marks = [];
        this.trailIntersections = [];
        this.world = world;

        // Complex behaviour for following trails like a real entity
        this.followingTrail = false; // Indicates if the entity is currently following a trail
        this.followedTrailEntityId = null; // The ID of the entity whose trail is being followed
        this.followedTrailIndex = 0; // The index in the followed trail where the entity is currently at
        // Each cycle should take only one follow step, for that reason, follow method needs to have temp memory
        // to be resumed each iteration.
        this.followCycles = 0;
    }

    addToTrail(position) {
        this.trail.push(position);
        this.world.trails.push(position);
        if (this.trail.length > MAX_TRAIL_LENGTH) {
            const shiftedPosition = this.trail.shift();
            this.world.shiftTrailPosition(shiftedPosition);
        }
    }

    addToMarks(mark) {
        this.marks.push(mark);
    }

    addToTrailIntersections(position) {
        this.trailIntersections.push(position);
    }

    preconditions(dx, dy) {
        let newPosition = new Position(this.x + dx, this.y + dy);
        // 1. Check if the new position matches any of our marks
        if (this.marks.some(mark =>
            Math.abs(mark.x - newPosition.x) < POINT_SIZE &&
            Math.abs(mark.y - newPosition.y) < POINT_SIZE
        )) {
            return false;
        }
        // 2. Check if the new position matches any of our trail points
        if (this.trail.some(pos =>
            Math.abs(pos.x - newPosition.x) < POINT_SIZE &&
            Math.abs(pos.y - newPosition.y) < POINT_SIZE
        )) {
            return false;
        }
        // 3. Check no other entity's position is the next position
        const others = this.world.getOtherEntities(this);
        for (const other of others) {
            if (other.isCollidingWith(newPosition)) {
                // console.error('Not moved because colliding with another entity');
                this.onOtherEntityCollision(other)
                // Allow entity to collide because we handle it on child classes
                // return false;
            }
        }

        return true;
    }

    isIntersectedWithTrail() {
        const newPosition = new Position(this.x, this.y, this);
        const others = this.world.getOtherEntities(this);
        // Check if the new position intersects with any other entity's trail
        for (const other of others) {
            if (other.trail.some(pos =>
                Math.abs(pos.x - newPosition.x) < POINT_SIZE &&
                Math.abs(pos.y - newPosition.y) < POINT_SIZE
            )) {
                // console.warn(`Entity ${this.id} has moved to (${newPosition.x}, ${newPosition.y}) intersecting with ${other.id}`);
                //Get the position of the intersection
                // takes time which is good for simulation as things takes time in real life

                return other;
            }
        }

        return false;
    }
    calculateSurroundingOffsets() {
        const searchRadius = POINT_SIZE * 1;
        const positions =  [
            [-searchRadius, -searchRadius],
            [0, -searchRadius],
            [searchRadius, -searchRadius],
            [-searchRadius, 0],
            [searchRadius, 0],
            [-searchRadius, searchRadius],
            [0, searchRadius],
            [searchRadius, searchRadius],
        ];
        // Randomize its order
        for (let i = positions.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [positions[i], positions[j]] = [positions[j], positions[i]];
        }
        // console.log(` Entity ${this.id} calculated surrounding offsets:`, positions);
        return positions;
    }

    resetFollow() {
        this.followingTrail = false;
        this.followedTrailEntityId = null;
        this.followedTrailIndex = 0;
        this.followCycles = 0;
        this.nearByPositionsCurrentIndex = 0;
        this.nearByFoundedTrailPoints = [];
        this.nearByPositions = [];
        console.log(`Entity ${this.id} has reset its follow state`);
    }

    follow() {
        // If first load of this method
        if (this.followCycles === 0) {
            // console.log('Started following cycles')
            this.nearByPositions = this.calculateSurroundingOffsets();
            this.nearByPositionsCurrentIndex = 0;
            this.nearByFoundedTrailPoints = [];
        }



        // There are no more positions to check
        if (this.nearByPositionsCurrentIndex >= this.nearByPositions.length) {
            // console.log('All position was checked before being able to find at least two points for the followed entity')
            this.resetFollow();
            return null;
        }

        const [dx, dy] = this.nearByPositions[this.nearByPositionsCurrentIndex];

        // Calculate the next index to check
        this.nearByPositionsCurrentIndex = (this.nearByPositionsCurrentIndex + 1) % this.nearByPositions.length;

        const isTrailPoint = this.world.hasTrailAtPosition(new Position(this.x + dx, this.y + dy));

        if (isTrailPoint) {
            if (this.followedTrailEntityId === isTrailPoint.getEntity().id) {
                // console.log('Trail point matched and added to nearByFoundedTrailPoints');
                this.nearByFoundedTrailPoints.push(isTrailPoint);
            }
            // console.log('Point was founded but did not match the followed entity')
        }
        
        let mostRecentTrailPoint = null;
        // If we have 2 points, compare both and return the most recent one
        if (this.nearByFoundedTrailPoints.length >= 2) {
            mostRecentTrailPoint = this.nearByFoundedTrailPoints.reduce((latest, current) => {
                return (current.timestamp > (latest?.timestamp ?? -Infinity)) ? current : latest;
            }, null);

        }
        // console.log('Most recent trail point:', mostRecentTrailPoint);

        // If no trail found, stop following
        this.followingTrail = false;
        this.followCycles += 1;
        return mostRecentTrailPoint
    }

    move() {
        // Fixes for class 
        const FRAME_WIDTH = this.world.width;
        const FRAME_HEIGHT = this.world.height;

        let dx, dy;
        let attemptCount = 0;
        const maxAttempts = 100;

        // Follow logic
        if (this.followingTrail) {
            const position = this.follow();

            if (!position) {
                // console.log('There is no move info for the moment')
                return
            }
            // Verify that new position is only POINT_SIZE away from the current position
            if (Math.abs(position.x - this.x) > POINT_SIZE || Math.abs(position.y - this.y) > POINT_SIZE) {
                // console.error(`Entity ${this.id} moved too far: dx=${position.x - this.x}, dy=${position.y - this.y}`);
                return;
            }
            // Verify this move is valid (3 steps)
            if (this.preconditions(this.x - position.x, this.y - position.y)){
                this.x = position.x;
                this.y = position.y;
            }


        } else {
            // if not following calculate a random valid movement
            while (attemptCount < maxAttempts) {
                dx = Math.floor(Math.random() * 21) - POINT_SIZE;
                dy = Math.floor(Math.random() * 21) - POINT_SIZE;

                if (this.preconditions(dx, dy)) break;
                attemptCount++;
            }
            // Verify that new position is only POINT_SIZE away from the current position
            if (Math.abs(dx) > POINT_SIZE || Math.abs(dy) > POINT_SIZE) {
                // console.error(`Entity ${this.id} moved too far: dx=${dx}, dy=${dy}`);
                return;
            }
            
            this.x = Math.max(0, Math.min(FRAME_WIDTH - POINT_SIZE, this.x + dx));
            this.y = Math.max(0, Math.min(FRAME_HEIGHT - POINT_SIZE, this.y + dy));
        }


        // Add the new position to the trail
        this.addToTrail(new Position(this.x, this.y, this));

        // Map limits
        let collidedEdge = null;
        if (this.x <= 0) collidedEdge = "left";
        else if (this.x >= FRAME_WIDTH - POINT_SIZE) collidedEdge = "right";
        else if (this.y <= 0) collidedEdge = "top";
        else if (this.y >= FRAME_HEIGHT - POINT_SIZE) collidedEdge = "bottom";

        // Paint collision with edges
        if (collidedEdge) {
            // PAINT PART
            // this.addToMarks(new CollisionMark(this.x, this.y, this.id, collidedEdge));
        }
        // Paint collision with trails
        const trailCollidedEntity = this.isIntersectedWithTrail()
        if (trailCollidedEntity) {
            // PAINT PART
            // this.addToTrailIntersections(new Position(this.x, this.y));

            // Interesting part is to attempt to follow others trail
            // console.log(`Entity ${this.id} collided with Entity ${trailCollidedEntity.id}`)
            console.log('Entity collided')
            // hay 2 opciones 
            // 1. Ha colisionado en medio del trail
            // 2. Casualmente ha colisionado con el último punto del trail
            // Encontrar el index del punto de colisión dentro del trail de trailCollidedEntity
            const collisionIndex = trailCollidedEntity.trail.findIndex(pos =>
                Math.abs(pos.x - this.x) < POINT_SIZE &&
                Math.abs(pos.y - this.y) < POINT_SIZE
            );
            if (collisionIndex !== -1) {
                // Si ha colisionado en medio del trail, seguir el trail desde ese punto
                // console.log(`Entity ${this.id} will follow Entity ${trailCollidedEntity.id}'s trail from index ${collisionIndex}`);
                this.followingTrail = true;
                this.followedTrailEntityId = trailCollidedEntity.id;
                this.followedTrailIndex = collisionIndex;

                // this.trail = trailCollidedEntity.trail.slice(collisionIndex);
            } else {
                // Si ha colisionado con el último punto del trail, seguir el trail completo
                // console.log(`Entity ${this.id} will follow Entity ${trailCollidedEntity.id}'s full trail`);
                // this.trail = [...trailCollidedEntity.trail];
                console.error('Not coded')
            }
        }
    }

    isCollidingWith(other) {
        return (
            Math.abs(this.x - other.x) < POINT_SIZE &&
            Math.abs(this.y - other.y) < POINT_SIZE
        );
    }

    onInteract(other) {
        console.log(`${this.constructor.name} ${this.id} interacted with ${other.constructor.name} ${other.id}`);
    }
    onOtherEntityCollision(other) {
        // Should be implemented on child class
    }
}
